import React from 'react';
import { ShoppingBag, GraduationCap, Sparkles, ExternalLink, Star, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRAND_CONFIG = {
  Amazon:    { accent: '#FF9900', icon: ShoppingBag },
  Flipkart:  { accent: '#2874F0', icon: ShoppingBag },
  Unacademy: { accent: '#10B981', icon: GraduationCap },
  Aakash:    { accent: '#06B6D4', icon: GraduationCap },
  Motion:    { accent: '#A855F7', icon: Sparkles },
};

const TIER_ACCENT = {
  Bronze: '#B45309',
  Silver: '#94A3B8',
  Gold: '#F59E0B',
  Platinum: '#38BDF8',
  Diamond: '#D946EF',
  Legendary: '#EF4444',
};

const RewardCard = ({ reward, onRedeem, userSuperPoints = 0, isRedeeming = false }) => {
  const isAffiliate = reward.redemption_type === 'affiliate';
  const isSuperPoints = reward.currency === 'super_points';
  const canAfford = userSuperPoints >= reward.cost;
  const config = BRAND_CONFIG[reward.brand] || { accent: 'rgb(var(--accent))', icon: ShoppingBag };
  const Icon = config.icon;
  const tierAccent = reward.tier ? TIER_ACCENT[reward.tier] : null;

  const handleAffiliateClick = () => {
    window.open(`${API_URL}/api/affiliate/click/${reward.id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="group relative overflow-hidden rounded-xl border transition-all hover:-translate-y-0.5 motion-fade-up"
      style={{
        background: 'rgb(var(--card))',
        borderColor: 'rgb(var(--border))',
      }}
      data-testid={`reward-card-${reward.id}`}
    >
      {/* Accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: config.accent }}
      />

      <div className="p-5">
        {/* Brand row */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
              style={{ background: `${config.accent}18` }}
            >
              <Icon className="w-4 h-4" style={{ color: config.accent }} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">{reward.brand}</p>
              <h3 className="text-sm font-semibold text-fg truncate leading-tight mt-0.5">{reward.name}</h3>
            </div>
          </div>

          {isSuperPoints && reward.tier && (
            <span
              className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest border"
              style={{
                background: `${tierAccent}18`,
                borderColor: `${tierAccent}55`,
                color: tierAccent,
              }}
            >
              {reward.tier}
            </span>
          )}
          {isAffiliate && (
            <span
              className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest border"
              style={{
                background: 'rgba(16,185,129,0.10)',
                borderColor: 'rgba(16,185,129,0.35)',
                color: 'rgb(16,185,129)',
              }}
            >
              Partner
            </span>
          )}
        </div>

        {reward.description && (
          <p className="text-xs text-fg-muted mb-4 line-clamp-2 min-h-[32px]">{reward.description}</p>
        )}

        {/* Price row */}
        {isAffiliate ? (
          <div className="flex items-center justify-between mb-4 pb-4 border-b" style={{ borderColor: 'rgb(var(--border))' }}>
            <span className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">Aspirant exclusive</span>
            <span className="text-xs font-bold" style={{ color: 'rgb(16,185,129)' }}>FREE</span>
          </div>
        ) : (
          <div className="flex items-baseline justify-between mb-4 pb-4 border-b" style={{ borderColor: 'rgb(var(--border))' }}>
            <span className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">Cost</span>
            <div className="flex items-baseline gap-1.5">
              <Star className="w-3.5 h-3.5 fill-current" style={{ color: 'rgb(var(--accent))' }} />
              <span className="text-2xl font-bold text-fg tracking-tight">{reward.cost.toLocaleString('en-IN')}</span>
              <span className="text-[11px] text-fg-muted">⭐ · ₹{Math.round(reward.cost * 0.5)}</span>
            </div>
          </div>
        )}

        {/* CTA */}
        {isAffiliate ? (
          <button
            onClick={handleAffiliateClick}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold text-white transition hover:brightness-110"
            style={{ background: 'rgb(16,185,129)' }}
            data-testid={`affiliate-button-${reward.id}`}
          >
            View on {reward.brand}
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            onClick={() => onRedeem(reward)}
            disabled={!canAfford || isRedeeming}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold transition ${
              canAfford
                ? 'text-white hover:brightness-110'
                : 'cursor-not-allowed'
            }`}
            style={{
              background: canAfford ? 'rgb(var(--accent))' : 'rgb(var(--card-hover))',
              color: canAfford ? '#fff' : 'rgb(var(--fg-faint))',
              border: canAfford ? 'none' : '1px solid rgb(var(--border))',
            }}
            data-testid={`redeem-button-${reward.id}`}
          >
            {isRedeeming ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Redeeming…</>
            ) : canAfford ? (
              <>Redeem now</>
            ) : (
              <>Need {(reward.cost - userSuperPoints).toLocaleString('en-IN')} more ⭐</>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default RewardCard;
